/**
 * Zod schemas for mask annotation extension.
 */
import { z } from "zod"

const maskInstancePolicySchema = z.object({
  mode: z.enum(["single", "multiple"]),
  min_instances: z.number().int().min(0).optional(),
  max_instances: z.number().int().min(1).nullable().optional()
})

const maskLabelSchema = z.object({
  key: z.string(),
  label: z.string(),
  color: z.string(),
  value: z.number().int().min(1).optional(),
  instance_policy: maskInstancePolicySchema
})

const maskGroupSchema = z.object({
  key: z.string(),
  name: z.string(),
  labels: z.array(maskLabelSchema).min(1)
})

const seriesFilterSchema = z.object({
  image_plane: z.array(z.enum(["transversal", "sagittal", "coronal"])).optional(),
  series_description: z.array(z.string().min(1)).optional(),
  number_of_frames: z
    .object({
      op: z.enum(["gte", "eq", "lte"]),
      value: z.number().int().min(0)
    })
    .optional()
})

export const maskConfigSchema = z
  .object({
    type: z.literal("mask"),
    groups: z.array(maskGroupSchema).optional(),
    labels: z.array(maskLabelSchema).optional(),
    series_filter: seriesFilterSchema.optional(),
    /**
     * When true, every voxel may be assigned to at most one mask label: newly
     * drawn voxels that overlap an existing label are clipped away on commit
     * (existing labels are protected). Absent/false preserves the legacy
     * behavior where masks may overlap freely.
     */
    disallow_overlap: z.boolean().optional()
  })
  .refine(
    (cfg) => {
      const fromGroups = cfg.groups?.flatMap((g) => g.labels) ?? []
      const fromLabels = cfg.labels ?? []
      return fromGroups.length + fromLabels.length > 0
    },
    { message: "Mask config must have at least one label (via groups or labels)." }
  )

export const createMaskSchema = z.object({
  project_module_id: z.string().min(1),
  series_id: z.string().min(1),
  label_name: z.string().min(1),
  label_value: z.number().int().min(0),
  storage_path: z.string().min(1)
})

export const updateMaskSchema = z.object({
  label_name: z.string().min(1).optional(),
  label_value: z.number().int().min(0).optional(),
  storage_path: z.string().min(1).optional()
})
